import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  BarChart,
  Bar,
  ComposedChart,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

import TopNavBar from '../components/TopNavBar';
import SideNavBar from '../components/SideNavBar';
import Refreshing from '../components/Refreshing';
import ChartCustomersByCountyMap from '../components/ChartCustomersByCountyMap';
import useIsMobile, { DESKTOP_CONTENT_MARGIN_LEFT, MOBILE_CONTENT_MARGIN_LEFT } from '../hooks/useIsMobile';

import * as apiComms from '../apiComms';
import * as logger from '../logger';
import * as dayjs from 'dayjs'

const PROG = 'UserDetails.js';
const BRANCH_DONUT_COLOURS = ['#087f8c', '#369fe3', '#20c997', '#f59f00', '#fa5252', '#845ef7', '#15aabf', '#82c91e'];
const IN_PROGRESS_STAMP_NUMBERS = [1, 2, 3, 4, 5, 6, 7, 8, 9];

const HIDDEN_USER_FIELDS = ['jwt'];
const COLLECTOR_CARD_FIELDS = [
  'live_collectors',
  'live_collectorcards',
  'closed_collectors',
  'closed_collectorcards'
];
const USER_FIELD_LABELS = {
  pushNotificationGoogleDeviceTokens : '# of Google Device Push Notification Tokens',
  pushNotificationAppleDeviceTokens : '# of Apple Device Push Notification Tokens',
  live_collectors : 'Live Collector Cards',
  live_collectorcards : 'Live Collector Cards',
  closed_collectors : 'Closed Collector Cards',
  closed_collectorcards : 'Closed Collector Cards'
};
const DATE_TIME_DISPLAY_FORMAT = 'DD/MMM/YYYY HH:mm';

const UserDetails = (props) => {
  const { accountId } = useParams();
  const isMobile = useIsMobile();

  const [user, setUser] = useState(null);
  const [userStats, setUserStats] = useState(null);
  const [coupons, setCoupons] = useState([]);
  const [isCouponsLoading, setIsCouponsLoading] = useState(true);
  const [couponsError, setCouponsError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(() => {
    const loadUser = async () => {
      const FUNCTION = 'loadUser';

      setIsLoading(true);
      setErrorMessage(null);

      try {
        const [_res, _statsRes] = await Promise.all([
          apiComms.getUser(accountId),
          apiComms.getUserStats(accountId)
        ]);
        logger.debug('Response from user APIs', { PROG, FUNCTION, _res, _statsRes });

        if (!_res || _res.isErr) {
          setUser(null);
          setUserStats(null);
          setErrorMessage(_res?.errMsg ?? 'Unable to load user.');
        }
        else {
          setUser(_res.response.user);
          if (_statsRes && !_statsRes.isErr) {
            setUserStats(_statsRes.response.stats);
          }
          else {
            setUserStats(null);
          }
        }
      }
      catch (e) {
        logger.warn('Exception Occured', { PROG, FUNCTION });
        console.log('Exception: '+e+', '+e.stack);
        setUser(null);
        setUserStats(null);
        setErrorMessage('Unable to load user.');
      }

      setIsLoading(false);
    };

    loadUser();
  }, [accountId]);

  useEffect(() => {
    let cancelled = false;
    setCoupons([]);
    setIsCouponsLoading(true);
    setCouponsError(null);

    apiComms.getUserCoupons(accountId).then((res) => {
      if (cancelled) return;
      if (res?.isErr || !Array.isArray(res?.response?.coupons)) {
        setCouponsError(res?.errMsg ?? 'Unable to load coupons.');
      }
      else {
        setCoupons(res.response.coupons);
      }
    }).catch(() => {
      if (!cancelled) setCouponsError('Unable to load coupons.');
    }).finally(() => {
      if (!cancelled) setIsCouponsLoading(false);
    });

    return () => { cancelled = true; };
  }, [accountId]);

  const handleBackToUsers = () => {
    props.handleUpdate({ updateType : 'navigate', route : '/users-users' });
  };

  const formatValue = (value) => {
    if (value == null) {
      return '';
    }

    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }

    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    }

    return String(value);
  };

  const getUserFieldLabel = (fieldName) => {
    return USER_FIELD_LABELS[fieldName] || fieldName;
  };

  const getPushTokenCount = (value) => {
    if (Array.isArray(value)) {
      return value.length;
    }

    if (value && typeof value === 'object') {
      return Object.keys(value).length;
    }

    if (typeof value === 'string' && value.trim() !== '') {
      return 1;
    }

    return 0;
  };

  const isPushTokenField = (fieldName) => {
    return fieldName === 'pushNotificationGoogleDeviceTokens' || fieldName === 'pushNotificationAppleDeviceTokens';
  };

  const isCollectorCardField = (fieldName) => {
    return COLLECTOR_CARD_FIELDS.includes(fieldName);
  };

  const formatCollectorCardCreatedOn = (collectorCard) => {
    const readableCreatedOn = collectorCard.createdOnReadable ?? collectorCard.created_on_readable ?? collectorCard.createdOnDateReadable;
    if (readableCreatedOn) {
      return readableCreatedOn;
    }

    const createdOn = collectorCard.createdOn ?? collectorCard.created_on ?? collectorCard.created;
    if (createdOn == null || createdOn === '') {
      return '';
    }

    if (typeof createdOn === 'number') {
      return dayjs(createdOn > 100000000000 ? createdOn : createdOn * 1000).format('DD/MMM/YYYY HH:mm');
    }

    const parsedDate = dayjs(createdOn);
    return parsedDate.isValid() ? parsedDate.format('DD/MMM/YYYY HH:mm') : String(createdOn);
  };

  const getCollectorCardType = (collectorCard) => {
    return collectorCard.cardId ??
      collectorCard.cardID ??
      collectorCard.collectorCardId ??
      collectorCard.collector_card_id ??
      collectorCard.card_id ??
      collectorCard.id ??
      'Collector Card';
  };

  const getCollectorCardTransactionIds = (collectorCard) => {
    const transactionIds = collectorCard.transactionIds ??
      collectorCard.transaction_ids ??
      collectorCard.transactions ??
      collectorCard.transactionids;

    return Array.isArray(transactionIds) ? transactionIds : [];
  };

  const renderCollectorCards = (collectorCards) => {
    if (!Array.isArray(collectorCards) || collectorCards.length === 0) {
      return <span style={ styles.mutedValue }>No collector cards</span>;
    }

    return (
      <div style={ styles.collectorCardsList }>
        { collectorCards.map((collectorCard, index) => {
          const cardType = getCollectorCardType(collectorCard);
          const cardId = collectorCard.esCardId ?? collectorCard.es_card_id;
          const createdOn = formatCollectorCardCreatedOn(collectorCard);
          const transactionIds = getCollectorCardTransactionIds(collectorCard);

          return (
            <div key={ `${cardType}-${index}` } style={ styles.collectorCardSummary }>
              <div style={ styles.collectorCardHeader }>
                <span style={ styles.collectorCardTitle }>{ cardType }</span>
                <span style={ styles.collectorCardBadge }>{ transactionIds.length } transactions</span>
              </div>
              { createdOn !== '' ? (
                <div style={ styles.collectorCardMeta }>Created On: { createdOn }</div>
              ) : null }
              { cardId ? <div style={ styles.collectorCardMeta }>Collector Card ID: { cardId }</div> : null }
              { collectorCard.redemptionId ? (
                <div style={ styles.collectorCardMeta }>
                  Coupon ID: <button type='button' style={ styles.couponLink } onClick={ () => props.handleUpdate({ updateType: 'navigate', route: '/coupons/lookup/' + encodeURIComponent(collectorCard.redemptionId) }) }>{ collectorCard.redemptionId }</button>
                </div>
              ) : null }
              { transactionIds.length > 0 ? (
                <div style={ styles.transactionIdList }>
                  { transactionIds.map((transactionId) => (
                    <span key={ transactionId } style={ styles.transactionIdPill }>{ transactionId }</span>
                  )) }
                </div>
              ) : (
                <div style={ styles.collectorCardMeta }>No transaction IDs recorded</div>
              ) }
            </div>
          );
        }) }
      </div>
    );
  };

  const renderCoupons = (couponItems) => {
    if (isCouponsLoading) return <span style={ styles.mutedValue }>Loading coupons...</span>;
    if (couponsError) return <span style={ styles.mutedValue }>{ couponsError }</span>;
    if (couponItems.length === 0) return <span style={ styles.mutedValue }>No coupons</span>;

    return (
      <div style={ styles.collectorCardsList }>
        { couponItems.map((coupon) => {
          const card = user?.closed_collectorcards?.find((closedCard) => closedCard.redemptionId === coupon.redemptionId);
          const transactionIds = Array.isArray(coupon.linkedTransactionIds) ? coupon.linkedTransactionIds : [];
          const createdOn = formatDateTimeValue(coupon.createdOn) || formatDateTimeValue(coupon.createdOnReadable);
          const redeemedOn = formatDateTimeValue(coupon.redeemedAndClosedOn) || formatDateTimeValue(coupon.redeemedAndClosedOnReadable);

          return (
            <div key={ coupon.redemptionId } style={ styles.collectorCardSummary }>
              <div style={ styles.collectorCardHeader }>
                <span style={ styles.collectorCardTitle }>
                  Coupon ID: <button type='button' style={ styles.couponLink } onClick={ () => props.handleUpdate({ updateType: 'navigate', route: '/coupons/lookup/' + encodeURIComponent(coupon.redemptionId) }) }>{ coupon.redemptionId }</button>
                </span>
                <span style={ styles.collectorCardBadge }>{ transactionIds.length } transactions</span>
              </div>
              { createdOn ? <div style={ styles.collectorCardMeta }>Created On: { createdOn }</div> : null }
              { coupon.isRedeemedAndClosed && redeemedOn ? <div style={ styles.collectorCardMeta }>Redeemed On: { redeemedOn }</div> : null }
              <div style={ styles.collectorCardMeta }>Collector Card ID: { card?.esCardId ?? 'Unavailable' }</div>
              <div style={ styles.collectorCardMeta }>Collector Card Type: { card ? getCollectorCardType(card) : 'Unavailable' }</div>
              { transactionIds.length > 0 ? (
                <div style={ styles.transactionIdList }>
                  { transactionIds.map((transactionId) => <span key={ transactionId } style={ styles.transactionIdPill }>{ transactionId }</span>) }
                </div>
              ) : <div style={ styles.collectorCardMeta }>No transaction IDs recorded</div> }
            </div>
          );
        }) }
      </div>
    );
  };

  const renderUserDataValue = (fieldName, value) => {
    if (isPushTokenField(fieldName)) {
      return getPushTokenCount(value);
    }

    if (isCollectorCardField(fieldName)) {
      return renderCollectorCards(value);
    }

    const formattedValue = formatValue(value);
    const isStructuredValue = typeof value === 'object' && value != null;

    if (isStructuredValue) {
      return <pre style={ styles.preformattedValue }>{ formattedValue }</pre>;
    }

    return formattedValue;
  };

  const getReadableCreatedOn = () => {
    if (!user) {
      return '';
    }

    return formatDateTimeValue(user.createdOn) || formatDateTimeValue(user.createdOnReadable);
  };

  const getDisplayName = () => {
    if (!user) {
      return accountId;
    }

    const firstName = user.meridian_firstName ?? user.meridian_firstname ?? '';
    const surname = user.meridian_surname ?? '';
    const fullName = `${firstName} ${surname}`.trim();

    if (fullName !== '') {
      return fullName;
    }

    return user.email ?? accountId;
  };

  const getVisibleUserEntries = () => {
    if (!user) {
      return [];
    }

    return Object.entries(user)
      .filter(([fieldName]) => !HIDDEN_USER_FIELDS.includes(fieldName))
      .sort(([fieldNameA], [fieldNameB]) => fieldNameA.localeCompare(fieldNameB));
  };

  const getCollectorCardStats = () => {
    if (userStats?.collectorCards?.summary) {
      const summary = userStats.collectorCards.summary;
      return {
        liveCollectorCardsCount : summary.liveCollectorCards ?? 0,
        closedCollectorCardsCount : summary.completedCollectorCards ?? 0,
        totalStampsEverCount : summary.totalStampsEver ?? 0,
        totalCollectorCardsCount : summary.totalCollectorCards ?? 0,
        completionRate : summary.completionRate ?? 0,
        averageStampsPerActiveCard : summary.averageStampsPerActiveCard ?? 0,
        nearestCardToCompletion : summary.nearestCardToCompletion ?? null,
        completedCollectorCardsLast90Days : summary.completedCollectorCardsLast90Days ?? 0,
        stampsLast30Days : summary.stampsLast30Days ?? 0,
        stampsLast90Days : summary.stampsLast90Days ?? 0,
        stampsPerWeekLast90Days : summary.stampsPerWeekLast90Days ?? 0
      };
    }

    if (!user) {
      return {
        liveCollectorCardsCount : 0,
        closedCollectorCardsCount : 0,
        totalStampsEverCount : 0,
        totalCollectorCardsCount : 0,
        completionRate : 0,
        averageStampsPerActiveCard : 0,
        nearestCardToCompletion : null,
        completedCollectorCardsLast90Days : 0,
        stampsLast30Days : 0,
        stampsLast90Days : 0,
        stampsPerWeekLast90Days : 0
      };
    }

    let liveCollectorCardsCount = 0;
    let closedCollectorCardsCount = 0;
    let totalStampsEverCount = 0;

    if (Array.isArray(user.live_collectorcards)) {
      liveCollectorCardsCount = user.live_collectorcards.length;
      user.live_collectorcards.forEach((collectorCard) => {
        if (Array.isArray(collectorCard.transactionIds)) {
          totalStampsEverCount += collectorCard.transactionIds.length;
        }
      });
    }

    if (Array.isArray(user.closed_collectorcards)) {
      closedCollectorCardsCount = user.closed_collectorcards.length;
      user.closed_collectorcards.forEach((collectorCard) => {
        if (Array.isArray(collectorCard.transactionIds)) {
          totalStampsEverCount += collectorCard.transactionIds.length;
        }
      });
    }

    return {
      liveCollectorCardsCount,
      closedCollectorCardsCount,
      totalStampsEverCount,
      totalCollectorCardsCount : liveCollectorCardsCount + closedCollectorCardsCount,
      completionRate : 0,
      averageStampsPerActiveCard : liveCollectorCardsCount > 0 ? totalStampsEverCount / liveCollectorCardsCount : 0,
      nearestCardToCompletion : null,
      completedCollectorCardsLast90Days : 0,
      stampsLast30Days : 0,
      stampsLast90Days : 0,
      stampsPerWeekLast90Days : 0
    };
  };

  const parseDailyChartData = (chartData) => {
    if (!Array.isArray(chartData)) {
      return [];
    }

    return chartData.map((chartDataRec) => ({
      date : dayjs(chartDataRec.x*1000).format('DD/MMM/YYYY'),
      nadur2 : chartDataRec.nadur2,
      nadur6 : chartDataRec.nadur6,
      nadur12 : chartDataRec.nadur12
    }));
  };

  const parseTransactionDailyChartData = (chartData) => {
    if (!Array.isArray(chartData)) {
      return [];
    }

    return chartData.map((chartDataRec) => ({
      x : chartDataRec.x,
      date : dayjs(chartDataRec.x*1000).format('DD/MMM/YYYY'),
      transactionCount : chartDataRec.transactionCount,
      totalSpend : Number(chartDataRec.totalSpend ?? 0)
    }));
  };

  const parseAppUsageDailyChartData = (chartData) => {
    if (!Array.isArray(chartData)) {
      return [];
    }

    return chartData.map((chartDataRec) => ({
      x : chartDataRec.x,
      date : dayjs(chartDataRec.x*1000).format('DD/MMM/YYYY'),
      pageViews : chartDataRec.pageViews,
      sessions : chartDataRec.sessions
    }));
  };

  const parseCardsInProgressByStampNumber = (chartData) => {
    const chartDataByStampNumber = {};

    if (Array.isArray(chartData)) {
      chartData.forEach((chartDataRec) => {
        const stampNumber = Number(chartDataRec.stampNumber);
        if (!IN_PROGRESS_STAMP_NUMBERS.includes(stampNumber)) {
          return;
        }

        chartDataByStampNumber[stampNumber] = chartDataRec;
      });
    }

    return IN_PROGRESS_STAMP_NUMBERS.map((stampNumber) => ({
      stampNumber,
      collectorCards : Number(chartDataByStampNumber[stampNumber]?.collectorCards ?? 0)
    }));
  };

  const formatPercent = (value) => {
    return `${Number(value ?? 0).toFixed(1)}%`;
  };

  const formatCurrency = (value) => {
    const numericValue = Number(value ?? 0);
    const safeValue = Number.isFinite(numericValue) ? numericValue : 0;

    return `€${safeValue.toLocaleString(undefined, { minimumFractionDigits : 2, maximumFractionDigits : 2 })}`;
  };

  const formatNumber = (value) => {
    return Number(value ?? 0).toLocaleString(undefined, { maximumFractionDigits : 1 });
  };

  const formatDateFromEpoch = (epoch) => {
    if (epoch == null) {
      return 'Never';
    }

    return dayjs(epoch*1000).format('DD/MMM/YYYY');
  };

  const formatDateTimeValue = (value) => {
    if (value == null || value === '') {
      return '';
    }

    const numericValue = Number(value);
    if (typeof value === 'number' || (typeof value === 'string' && value.trim() !== '' && Number.isFinite(numericValue))) {
      const milliseconds = numericValue > 9999999999 ? numericValue : numericValue*1000;
      return dayjs(milliseconds).format(DATE_TIME_DISPLAY_FORMAT);
    }

    const parsedDate = dayjs(value);
    if (!parsedDate.isValid()) {
      return String(value);
    }

    return parsedDate.format(DATE_TIME_DISPLAY_FORMAT);
  };

  const parseSpendBreakdownChartData = (chartData, labelKey) => {
    if (!Array.isArray(chartData)) {
      return [];
    }

    return chartData.map((chartDataRec) => ({
      name : chartDataRec[labelKey],
      totalSpend : Number(chartDataRec.totalSpend ?? 0),
      transactions : Number(chartDataRec.transactions ?? 0),
      quantity : Number(chartDataRec.quantity ?? 0)
    }));
  };

  const getPurchaseAndAppUsageChartData = () => {
    const chartDataByDate = {};

    transactionsPerDay.forEach((chartDataRec) => {
      chartDataByDate[chartDataRec.date] = {
        x : chartDataRec.x,
        date : chartDataRec.date,
        transactions : chartDataRec.transactionCount,
        sessions : 0,
        pageViews : 0
      };
    });

    appUsagePerDay.forEach((chartDataRec) => {
      if (!chartDataByDate[chartDataRec.date]) {
        chartDataByDate[chartDataRec.date] = {
          x : chartDataRec.x,
          date : chartDataRec.date,
          transactions : 0,
          sessions : 0,
          pageViews : 0
        };
      }
      chartDataByDate[chartDataRec.date].x = chartDataByDate[chartDataRec.date].x || chartDataRec.x;
      chartDataByDate[chartDataRec.date].sessions = chartDataRec.sessions;
      chartDataByDate[chartDataRec.date].pageViews = chartDataRec.pageViews;
    });

    return Object.values(chartDataByDate).sort((a, b) => a.x - b.x);
  };

  const renderSpendBarChart = (chartData, title, colour = '#369fe3') => {
    return (
      <div className='card' style={ styles.chartCard }>
        <div style={ styles.chartContainer }>
          { chartData.length > 0 ? (
            <ResponsiveContainer>
              <BarChart data={ chartData } margin={{ top: 20, right: 20, left: 6, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" angle={45} textAnchor="start" height={70} tick={{ fontSize: 10 }} />
                <YAxis width={58} tickFormatter={ (value) => `€${value}` } />
                <Tooltip formatter={ (value, name) => {
                  if (name === 'totalSpend') {
                    return [formatCurrency(value), 'Total Spend'];
                  }
                  return [value, name];
                }} />
                <Bar dataKey="totalSpend" fill={ colour } radius={[4, 4, 0, 0]} name="Total Spend" />
              </BarChart>
            </ResponsiveContainer>
          ) : null }
        </div>
        <div className='card-body'>{ title }</div>
      </div>
    );
  };

  const renderSpendDonutChart = (chartData, title) => {
    const donutData = chartData.filter((chartDataRec) => Number(chartDataRec.totalSpend ?? 0) > 0);
    const totalSpend = donutData.reduce((total, chartDataRec) => total + Number(chartDataRec.totalSpend ?? 0), 0);

    return (
      <div className='card' style={ styles.chartCard }>
        <div style={ styles.chartContainer }>
          { donutData.length > 0 ? (
            <div style={ styles.donutChartWrapper }>
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={ donutData }
                    dataKey='totalSpend'
                    nameKey='name'
                    innerRadius={70}
                    outerRadius={108}
                    paddingAngle={2}
                  >
                    { donutData.map((chartDataRec, index) => (
                      <Cell key={ chartDataRec.name } fill={ BRANCH_DONUT_COLOURS[index % BRANCH_DONUT_COLOURS.length] } />
                    )) }
                  </Pie>
                  <Tooltip
                    formatter={ (value, name, tooltipProps) => {
                      const percentage = totalSpend > 0 ? (Number(value) / totalSpend) * 100 : 0;
                      const transactions = tooltipProps?.payload?.transactions ?? 0;
                      return [`${formatCurrency(value)} (${percentage.toFixed(1)}%, ${formatNumber(transactions)} transactions)`, name];
                    }}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
              <div style={ styles.donutCenterLabel }>
                <div style={ styles.donutCenterValue }>{ formatCurrency(totalSpend) }</div>
                <div style={ styles.donutCenterText }>Total Spend</div>
              </div>
            </div>
          ) : null }
        </div>
        <div className='card-body'>{ title }</div>
      </div>
    );
  };

  const getUserSpendByCountyData = () => {
    if (!user || !user.meridian_county) {
      return [];
    }

    const totalSpend = Number(userStats?.totalSpend ?? 0);
    if (totalSpend <= 0) {
      return [];
    }

    return [
      {
        county : user.meridian_county,
        totalSpend
      }
    ];
  };

  const collectorCardStats = getCollectorCardStats();
  const collectorCardAnalytics = userStats?.collectorCards ?? {};
  const byCollectorCard = collectorCardAnalytics.byCollectorCard ?? [];
  const byStampNumber = parseCardsInProgressByStampNumber(collectorCardAnalytics.byStampNumber);
  const stampsPerDay = parseDailyChartData(collectorCardAnalytics.stampsPerDay);
  const completedPerDay = parseDailyChartData(collectorCardAnalytics.completedPerDay);
  const transactionsPerDay = parseTransactionDailyChartData(userStats?.transactionsPerDay);
  const appUsagePerDay = parseAppUsageDailyChartData(userStats?.appUsagePerDay);
  const transactionInsights = userStats?.transactionInsights ?? {};
  const appUsageInsights = userStats?.appUsageInsights ?? {};
  const spendByBranch = parseSpendBreakdownChartData(transactionInsights.spendByBranch, 'branchCode');
  const spendByMainGroup = parseSpendBreakdownChartData(transactionInsights.spendByMainGroup, 'name');
  const spendBySubGroup = parseSpendBreakdownChartData(transactionInsights.spendBySubGroup, 'name');
  const spendBySupplier = parseSpendBreakdownChartData(transactionInsights.spendBySupplier, 'name');
  const purchaseAndAppUsageChartData = getPurchaseAndAppUsageChartData();
  const topProducts = Array.isArray(transactionInsights.topProducts) ? transactionInsights.topProducts : [];
  const nearestCardToCompletion = collectorCardStats.nearestCardToCompletion;
  const userSpendByCountyData = getUserSpendByCountyData();
  const userSpendWithCounty = userSpendByCountyData.reduce((total, countyRec) => total + Number(countyRec.totalSpend || 0), 0);

  return (
    <>
      <TopNavBar handleUpdate = { props.handleUpdate } activeRoute = { props.activeRoute } />
      <SideNavBar handleUpdate = { props.handleUpdate } activeRoute = { props.activeRoute } />
      <div style={{ marginLeft: isMobile ? MOBILE_CONTENT_MARGIN_LEFT : DESKTOP_CONTENT_MARGIN_LEFT, marginRight : 10, marginTop : 0, paddingTop : 45 }}>
        <div style={{ paddingTop : 20, marginBottom : 15 }}>
          <button
            type='button'
            onClick={ handleBackToUsers }
            style={ styles.breadcrumbButton }
          >
            Users & Engagement &gt; Users
          </button>
          <span style={ styles.breadcrumbCurrent }> &gt; User Details</span>
        </div>

        { isLoading ? (
          <Refreshing message='Loading user...' />
        ) : null }

        { !isLoading && errorMessage != null ? (
          <div className='alert alert-warning' role='alert'>
            { errorMessage }
          </div>
        ) : null }

        { !isLoading && user != null ? (
          <>
            <div style={ styles.summaryPanel }>
              <h1 style={ styles.title }>{ getDisplayName() }</h1>
              <div style={ styles.headingStatsGrid }>
                <div style={ styles.headingStatCard }>
                  <div style={ styles.headingStatValue }>{ collectorCardStats.liveCollectorCardsCount }</div>
                  <div style={ styles.headingStatLabel }>Live Collector Cards</div>
                </div>
                <div style={ styles.headingStatCard }>
                  <div style={ styles.headingStatValue }>{ collectorCardStats.closedCollectorCardsCount }</div>
                  <div style={ styles.headingStatLabel }>Closed Collector Cards</div>
                </div>
                <div style={ styles.headingStatCard }>
                  <div style={ styles.headingStatValue }>{ collectorCardStats.totalStampsEverCount }</div>
                  <div style={ styles.headingStatLabel }>Total Stamps Ever</div>
                </div>
                <div style={ styles.headingStatCard }>
                  <div style={ styles.headingStatValue }>{ collectorCardStats.totalCollectorCardsCount }</div>
                  <div style={ styles.headingStatLabel }>Total Collector Cards</div>
                </div>
                <div style={ styles.headingStatCard }>
                  <div style={ styles.headingStatValue }>{ formatPercent(collectorCardStats.completionRate) }</div>
                  <div style={ styles.headingStatLabel }>Collector Card Completion Rate</div>
                </div>
                <div style={ styles.headingStatCard }>
                  <div style={ styles.headingStatValue }>{ formatCurrency(transactionInsights.averageTransactionValue) }</div>
                  <div style={ styles.headingStatLabel }>Average Transaction Value</div>
                </div>
                <div style={ styles.headingStatCard }>
                  <div style={ styles.headingStatValue }>{ formatCurrency(transactionInsights.spendLast30Days) }</div>
                  <div style={ styles.headingStatLabel }>Spend Last 30 Days</div>
                </div>
                <div style={ styles.headingStatCard }>
                  <div style={ styles.headingStatValue }>{ formatCurrency(transactionInsights.spendLast90Days) }</div>
                  <div style={ styles.headingStatLabel }>Spend Last 90 Days</div>
                </div>
                <div style={ styles.headingStatCard }>
                  <div style={ styles.headingStatDateValue }>{ formatDateFromEpoch(transactionInsights.lastPurchaseEpoch) }</div>
                  <div style={ styles.headingStatLabel }>Last Purchase Date</div>
                </div>
                <div style={ styles.headingStatCard }>
                  <div style={ styles.headingStatValue }>{ transactionInsights.daysSinceLastPurchase == null ? 'N/A' : formatNumber(transactionInsights.daysSinceLastPurchase) }</div>
                  <div style={ styles.headingStatLabel }>Days Since Last Purchase</div>
                </div>
                <div style={ styles.headingStatCard }>
                  <div style={ styles.headingStatDateValue }>{ formatDateFromEpoch(appUsageInsights.lastAppUsageEpoch) }</div>
                  <div style={ styles.headingStatLabel }>Last App Usage</div>
                </div>
                <div style={ styles.headingStatCard }>
                  <div style={ styles.headingStatValue }>{ formatNumber(appUsageInsights.sessionsLast30Days) }</div>
                  <div style={ styles.headingStatLabel }>App Sessions Last 30 Days</div>
                </div>
                <div style={ styles.headingStatCard }>
                  <div style={ styles.headingStatValue }>{ formatNumber(transactionInsights.purchaseDayCount) }</div>
                  <div style={ styles.headingStatLabel }>Distinct Purchase Days</div>
                </div>
                <div style={ styles.headingStatCard }>
                  <div style={ styles.headingStatValue }>{ transactionInsights.averageDaysBetweenPurchaseDays == null ? 'N/A' : formatNumber(transactionInsights.averageDaysBetweenPurchaseDays) }</div>
                  <div style={ styles.headingStatLabel }>Avg Days Between Purchase Days</div>
                </div>
                <div style={ styles.headingStatCard }>
                  <div style={ styles.headingStatValue }>{ formatNumber(collectorCardStats.averageStampsPerActiveCard) }</div>
                  <div style={ styles.headingStatLabel }>Avg Stamps Per Active Card</div>
                </div>
                <div style={ styles.headingStatCard }>
                  <div style={ styles.headingStatValue }>{ formatNumber(collectorCardStats.completedCollectorCardsLast90Days) }</div>
                  <div style={ styles.headingStatLabel }>Cards Completed Last 90 Days</div>
                </div>
                <div style={ styles.headingStatCard }>
                  <div style={ styles.headingStatValue }>{ formatNumber(collectorCardStats.stampsPerWeekLast90Days) }</div>
                  <div style={ styles.headingStatLabel }>Stamp Velocity Per Week</div>
                </div>
              </div>
              <div style={ styles.summaryGrid }>
                <div>
                  <div style={ styles.summaryLabel }>Account ID</div>
                  <div style={ styles.summaryValue }>{ user.meridian_accountKey }</div>
                </div>
                <div>
                  <div style={ styles.summaryLabel }>Email</div>
                  <div style={ styles.summaryValue }>{ user.email }</div>
                </div>
                <div>
                  <div style={ styles.summaryLabel }>County</div>
                  <div style={ styles.summaryValue }>{ user.meridian_county ?? '' }</div>
                </div>
                <div>
                  <div style={ styles.summaryLabel }>Signed-Up On</div>
                  <div style={ styles.summaryValue }>{ getReadableCreatedOn() }</div>
                </div>
              </div>
            </div>

            <div style={ styles.fullWidthChart }>
              <ChartCustomersByCountyMap
                metricType='spend'
                countyData={ userSpendByCountyData }
                totalMetricWithCounty={ userSpendWithCounty }
                eyebrow='User Geography'
                title='User Total Spend By Home County'
                cardTitle='User Total Spend By Home County'
                totalPillLabel='all-time spend'
              />
            </div>

            <div style={ styles.insightGrid }>
              <div className='card' style={ styles.insightCard }>
                <div style={ styles.insightLabel }>Favourite Branch</div>
                <div style={ styles.insightValue }>{ transactionInsights.favouriteBranch?.branchCode ?? 'N/A' }</div>
                <div style={ styles.insightMeta }>{ transactionInsights.favouriteBranch ? `${formatCurrency(transactionInsights.favouriteBranch.totalSpend)} across ${formatNumber(transactionInsights.favouriteBranch.transactions)} transactions` : 'No transaction data' }</div>
              </div>
              <div className='card' style={ styles.insightCard }>
                <div style={ styles.insightLabel }>Favourite Category</div>
                <div style={ styles.insightValue }>{ transactionInsights.favouriteMainGroup?.name ?? 'N/A' }</div>
                <div style={ styles.insightMeta }>{ transactionInsights.favouriteMainGroup ? `${formatCurrency(transactionInsights.favouriteMainGroup.totalSpend)} total spend` : 'No category data' }</div>
              </div>
              <div className='card' style={ styles.insightCard }>
                <div style={ styles.insightLabel }>Nearest Card To Completion</div>
                <div style={ styles.insightValue }>{ nearestCardToCompletion?.collectorCardId ?? 'N/A' }</div>
                <div style={ styles.insightMeta }>
                  { nearestCardToCompletion ?
                    `${formatNumber(nearestCardToCompletion.currentStampNumber)} / ${formatNumber(nearestCardToCompletion.totalStampsToCompleteCard)} stamps, ${formatNumber(nearestCardToCompletion.stampsRemaining)} remaining` :
                    'No active collector card progress found' }
                </div>
              </div>
              <div className='card' style={ styles.insightCard }>
                <div style={ styles.insightLabel }>App Engagement Last 30 Days</div>
                <div style={ styles.insightValue }>{ formatNumber(appUsageInsights.pageViewsLast30Days) }</div>
                <div style={ styles.insightMeta }>{ `${formatNumber(appUsageInsights.sessionsLast30Days)} sessions, ${formatNumber(appUsageInsights.daysSinceLastAppUsage)} days since last app usage` }</div>
              </div>
            </div>

            <div style={ styles.chartGrid }>
              <div className='card' style={ styles.chartCard }>
                <div style={ styles.chartContainer }>
                  { purchaseAndAppUsageChartData.length > 0 ? (
                    <ResponsiveContainer>
                      <ComposedChart data={ purchaseAndAppUsageChartData } margin={{ top: 20, right: 20, left: 6, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" angle={45} textAnchor="start" height={60} tick={{ fontSize: 10 }} />
                        <YAxis width={42} />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="transactions" fill="#f59f00" name="Transactions" radius={[4, 4, 0, 0]} />
                        <Line type="monotone" dataKey="sessions" stroke="#20c997" strokeWidth={2} dot={false} name="App Sessions" />
                        <Line type="monotone" dataKey="pageViews" stroke="#369fe3" strokeWidth={2} dot={false} name="Page Views" />
                      </ComposedChart>
                    </ResponsiveContainer>
                  ) : null }
                </div>
                <div className='card-body'>Purchases Vs App Usage Over Time</div>
              </div>

              { renderSpendDonutChart(spendByBranch, 'Spend By Branch') }
              { renderSpendBarChart(spendByMainGroup, 'Spend By Main Product Category', '#369fe3') }
              { renderSpendBarChart(spendBySubGroup, 'Spend By Product Sub-Category', '#20c997') }
              { renderSpendBarChart(spendBySupplier, 'Spend By Supplier', '#f59f00') }

              <div className='card' style={ styles.chartCard }>
                <div style={ styles.chartContainer }>
                  { appUsagePerDay.length > 0 ? (
                    <ResponsiveContainer>
                      <LineChart data={ appUsagePerDay } margin={{ top: 20, right: 20, left: 6, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" angle={45} textAnchor="start" height={60} tick={{ fontSize: 10 }} />
                        <YAxis width={42} />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="pageViews" stroke="#369fe3" strokeWidth={2} dot={false} name="Page Views" />
                        <Line type="monotone" dataKey="sessions" stroke="#20c997" strokeWidth={2} dot={false} name="Sessions" />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : null }
                </div>
                <div className='card-body'>User App Usage Per Day</div>
              </div>

              <div className='card' style={ styles.chartCard }>
                <div style={ styles.chartContainer }>
                  { transactionsPerDay.length > 0 ? (
                    <ResponsiveContainer>
                      <ComposedChart data={ transactionsPerDay } margin={{ top: 20, right: 20, left: 6, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" angle={45} textAnchor="start" height={60} tick={{ fontSize: 10 }} />
                        <YAxis yAxisId="left" width={42} />
                        <YAxis yAxisId="right" orientation="right" width={52} tickFormatter={ (value) => `€${value}` } />
                        <Tooltip
                          formatter={ (value, name) => {
                            if (name === 'totalSpend') {
                              return [`€${Number(value).toFixed(2)}`, 'Total Spend'];
                            }
                            return [value, 'Transactions'];
                          }}
                        />
                        <Legend />
                        <Bar yAxisId="left" dataKey="transactionCount" fill="#369fe3" name="Transactions" radius={[4, 4, 0, 0]} />
                        <Line yAxisId="right" type="monotone" dataKey="totalSpend" stroke="#f59f00" strokeWidth={2} dot={false} name="Total Spend" />
                      </ComposedChart>
                    </ResponsiveContainer>
                  ) : null }
                </div>
                <div className='card-body'>User Transactions By Date</div>
              </div>

              <div className='card' style={ styles.chartCard }>
                <div style={ styles.chartContainer }>
                  { byCollectorCard.length > 0 ? (
                    <ResponsiveContainer>
                      <BarChart data={ byCollectorCard } margin={{ top: 20, right: 20, left: 6, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="collectorCardId" />
                        <YAxis width={42} />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="stamps" fill="#369fe3" name="Stamps" />
                        <Bar dataKey="collectorCards" fill="#20c997" name="Collector Cards" />
                        <Bar dataKey="completedCollectorCards" fill="#f59f00" name="Completed Cards" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : null }
                </div>
                <div className='card-body'>User Collector Cards By Card Type</div>
              </div>

              <div className='card' style={ styles.chartCard }>
                <div style={ styles.chartContainer }>
                  { byStampNumber.length > 0 ? (
                    <ResponsiveContainer>
                      <BarChart data={ byStampNumber } margin={{ top: 20, right: 20, left: 6, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="stampNumber" label={{ value: 'Current Stamp Number', position: 'insideBottom', offset: -2 }} />
                        <YAxis width={42} />
                        <Tooltip />
                        <Bar dataKey="collectorCards" fill="#20c997" radius={[4, 4, 0, 0]} name="Collector Cards" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : null }
                </div>
                <div className='card-body'>User Cards In Progress By Stamp Number</div>
              </div>

              <div className='card' style={ styles.chartCard }>
                <div style={ styles.chartContainer }>
                  { stampsPerDay.length > 0 ? (
                    <ResponsiveContainer>
                      <LineChart data={ stampsPerDay } margin={{ top: 20, right: 20, left: 6, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" angle={45} textAnchor="start" height={60} tick={{ fontSize: 10 }} />
                        <YAxis width={42} />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="nadur2" stroke="#369fe3" strokeWidth={2} dot={false} name="NADUR-2" />
                        <Line type="monotone" dataKey="nadur6" stroke="#20c997" strokeWidth={2} dot={false} name="NADUR-6" />
                        <Line type="monotone" dataKey="nadur12" stroke="#f59f00" strokeWidth={2} dot={false} name="NADUR-12" />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : null }
                </div>
                <div className='card-body'>User Stamps Collected Per Day</div>
              </div>

              <div className='card' style={ styles.chartCard }>
                <div style={ styles.chartContainer }>
                  { completedPerDay.length > 0 ? (
                    <ResponsiveContainer>
                      <LineChart data={ completedPerDay } margin={{ top: 20, right: 20, left: 6, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" angle={45} textAnchor="start" height={60} tick={{ fontSize: 10 }} />
                        <YAxis width={42} />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="nadur2" stroke="#369fe3" strokeWidth={2} dot={false} name="NADUR-2" />
                        <Line type="monotone" dataKey="nadur6" stroke="#20c997" strokeWidth={2} dot={false} name="NADUR-6" />
                        <Line type="monotone" dataKey="nadur12" stroke="#f59f00" strokeWidth={2} dot={false} name="NADUR-12" />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : null }
                </div>
                <div className='card-body'>User Collector Cards Completed Per Day</div>
              </div>
            </div>

            <div className='card' style={{ marginTop : 20 }}>
              <div className='card-body'>
                <h2 style={ styles.sectionTitle }>Top Products Bought</h2>
                <div style={{ overflowX : 'auto' }}>
                  <table className='table' style={{ minWidth : 760 }}>
                    <thead>
                      <tr>
                        <th>PLU</th>
                        <th>Description</th>
                        <th>Category</th>
                        <th>Supplier</th>
                        <th style={{ textAlign : 'right' }}>Quantity</th>
                        <th style={{ textAlign : 'right' }}>Transactions</th>
                        <th style={{ textAlign : 'right' }}>Total Spend</th>
                      </tr>
                    </thead>
                    <tbody>
                      { topProducts.length > 0 ? topProducts.map((product) => (
                        <tr key={ product.pluCode }>
                          <td>{ product.pluCode }</td>
                          <td>{ product.description }</td>
                          <td>{ [product.mainGroup, product.subGroup].filter(Boolean).join(' / ') }</td>
                          <td>{ product.supplier }</td>
                          <td style={{ textAlign : 'right' }}>{ formatNumber(product.quantity) }</td>
                          <td style={{ textAlign : 'right' }}>{ formatNumber(product.transactions) }</td>
                          <td style={{ textAlign : 'right' }}>{ formatCurrency(product.totalSpend) }</td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan='7' style={ styles.emptyTableCell }>No product transaction data found.</td>
                        </tr>
                      ) }
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className='card' style={{ marginTop : 20 }}>
              <div className='card-body'>
                <h2 style={ styles.sectionTitle }>Non-Redeemed Coupons ({ coupons.filter((coupon) => !coupon.isRedeemedAndClosed).length })</h2>
                { renderCoupons(coupons.filter((coupon) => !coupon.isRedeemedAndClosed)) }
              </div>
            </div>

            <div className='card' style={{ marginTop : 20 }}>
              <div className='card-body'>
                <h2 style={ styles.sectionTitle }>Redeemed Coupons ({ coupons.filter((coupon) => coupon.isRedeemedAndClosed).length })</h2>
                { renderCoupons(coupons.filter((coupon) => coupon.isRedeemedAndClosed)) }
              </div>
            </div>

            <div className='card' style={{ marginTop : 20 }}>
              <div className='card-body'>
                <h2 style={ styles.sectionTitle }>User Data</h2>
                <div style={{ overflowX : 'auto' }}>
                  <table className='table' style={{ minWidth : 700 }}>
                    <tbody>
                      { getVisibleUserEntries().map(([fieldName, value]) => {
                        return (
                          <tr key={ fieldName }>
                            <th style={ styles.fieldNameCell }>{ getUserFieldLabel(fieldName) }</th>
                            <td style={ styles.fieldValueCell }>
                              { renderUserDataValue(fieldName, value) }
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        ) : null }
      </div>
    </>
  );
};

const styles = {
  breadcrumbButton : {
    background : 'none',
    border : 'none',
    padding : 0,
    color : '#0d6efd',
    cursor : 'pointer',
    fontSize : 14
  },
  breadcrumbCurrent : {
    color : '#6c757d',
    fontSize : 14
  },
  summaryPanel : {
    border : '1px solid #dee2e6',
    borderRadius : 6,
    padding : 20,
    backgroundColor : 'white'
  },
  title : {
    fontSize : 22,
    fontWeight : 'bold',
    marginBottom : 18
  },
  headingStatsGrid : {
    display : 'grid',
    gridTemplateColumns : 'repeat(auto-fit, minmax(180px, 1fr))',
    gap : 12,
    marginBottom : 22
  },
  headingStatCard : {
    border : '1px solid #e3e8ef',
    borderRadius : 6,
    padding : '16px 18px',
    backgroundColor : '#f8fbfd'
  },
  headingStatValue : {
    fontSize : 'clamp(22px, 2vw, 30px)',
    lineHeight : 1,
    fontWeight : 800,
    color : '#009fe3',
    marginBottom : 8,
    overflowWrap : 'anywhere'
  },
  headingStatDateValue : {
    fontSize : 'clamp(18px, 1.45vw, 23px)',
    lineHeight : 1.08,
    fontWeight : 800,
    color : '#009fe3',
    marginBottom : 8,
    overflowWrap : 'anywhere'
  },
  headingStatLabel : {
    fontSize : 13,
    color : '#495057',
    fontWeight : 'bold'
  },
  summaryGrid : {
    display : 'grid',
    gridTemplateColumns : 'repeat(auto-fit, minmax(220px, 1fr))',
    gap : 16
  },
  summaryLabel : {
    fontSize : 12,
    color : '#6c757d',
    marginBottom : 4
  },
  summaryValue : {
    fontSize : 15,
    fontWeight : 'bold',
    wordBreak : 'break-word'
  },
  chartGrid : {
    display : 'grid',
    gridTemplateColumns : 'repeat(auto-fit, minmax(min(100%, 420px), 1fr))',
    gap : 20,
    marginTop : 20
  },
  insightGrid : {
    display : 'grid',
    gridTemplateColumns : 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))',
    gap : 14,
    marginTop : 20
  },
  insightCard : {
    padding : 16,
    border : '1px solid #e3e8ef',
    backgroundColor : 'white'
  },
  insightLabel : {
    fontSize : 12,
    textTransform : 'uppercase',
    letterSpacing : 0.8,
    color : '#6c757d',
    fontWeight : 800,
    marginBottom : 8
  },
  insightValue : {
    fontSize : 22,
    fontWeight : 800,
    color : '#153243',
    marginBottom : 6,
    wordBreak : 'break-word'
  },
  insightMeta : {
    fontSize : 13,
    color : '#495057'
  },
  fullWidthChart : {
    marginTop : 20
  },
  chartCard : {
    minWidth : 0
  },
  chartContainer : {
    height : 300,
    width : '100%'
  },
  donutChartWrapper : {
    height : '100%',
    position : 'relative',
    width : '100%'
  },
  donutCenterLabel : {
    position : 'absolute',
    top : '50%',
    left : '50%',
    transform : 'translate(-50%, -58%)',
    textAlign : 'center',
    pointerEvents : 'none'
  },
  donutCenterValue : {
    fontSize : 20,
    fontWeight : 800,
    color : '#153243',
    lineHeight : 1.1
  },
  donutCenterText : {
    fontSize : 12,
    color : '#6c757d',
    marginTop : 3
  },
  sectionTitle : {
    fontSize : 18,
    fontWeight : 'bold',
    marginBottom : 14
  },
  fieldNameCell : {
    width : 240,
    whiteSpace : 'nowrap',
    color : '#495057'
  },
  fieldValueCell : {
    wordBreak : 'break-word'
  },
  emptyTableCell : {
    textAlign : 'center',
    color : '#6c757d',
    padding : 18
  },
  preformattedValue : {
    margin : 0,
    whiteSpace : 'pre-wrap',
    wordBreak : 'break-word'
  },
  mutedValue : {
    color : '#6c757d'
  },
  collectorCardsList : {
    display : 'grid',
    gap : 10
  },
  collectorCardSummary : {
    border : '1px solid #e3e8ef',
    borderRadius : 8,
    padding : 12,
    backgroundColor : '#f8fbfd'
  },
  collectorCardHeader : {
    display : 'flex',
    justifyContent : 'space-between',
    gap : 10,
    alignItems : 'center',
    flexWrap : 'wrap',
    marginBottom : 6
  },
  collectorCardTitle : {
    fontWeight : 800,
    color : '#153243'
  },
  collectorCardBadge : {
    borderRadius : 999,
    padding : '4px 9px',
    backgroundColor : '#e7f3ff',
    color : '#087f8c',
    fontSize : 12,
    fontWeight : 800
  },
  collectorCardMeta : {
    fontSize : 13,
    color : '#495057',
    marginBottom : 8
  },
  couponLink : {
    background : 'none',
    border : 'none',
    padding : 0,
    color : '#087f8c',
    font : 'inherit',
    fontWeight : 800,
    cursor : 'pointer',
    textDecoration : 'underline'
  },
  transactionIdList : {
    display : 'flex',
    flexWrap : 'wrap',
    gap : 6
  },
  transactionIdPill : {
    borderRadius : 6,
    padding : '4px 7px',
    backgroundColor : 'white',
    border : '1px solid #dbe5ed',
    color : '#495057',
    fontSize : 12,
    fontFamily : 'monospace'
  }
};

export default UserDetails;
